import { useState, useCallback, useRef } from "react";
import type { CellKey } from "../types";
import { createCellKey, parseCellKey } from "../utils";

interface DragState {
  isActive: boolean; // True when drag has actually started (moved to different cell)
  startHour: number;
  startDay: number;
  shiftKey: boolean;
  baseSelection: Set<CellKey>;
}

interface UseDaypartingSelectionReturn {
  selectedCells: Set<CellKey>;
  selectedCount: number;
  isDragging: boolean;
  isPointerDown: boolean;
  toggleCell: (hour: number, day: number, shiftKey: boolean) => void;
  clearSelection: () => void;
  startDrag: (hour: number, day: number, shiftKey: boolean) => void;
  updateDrag: (hour: number, day: number) => void;
  endDrag: () => void;
}

export function useDaypartingSelection(): UseDaypartingSelectionReturn {
  const [selectedCells, setSelectedCells] = useState(new Set<CellKey>());
  const [lastSelected, setLastSelected] = useState<CellKey | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const dragState = useRef<DragState | null>(null);

  const toggleCell = useCallback(
    (hour: number, day: number, shiftKey: boolean) => {
      const key = createCellKey(hour, day);

      setSelectedCells((prev) => {
        const next = new Set(prev);

        if (shiftKey && lastSelected) {
          // Select all cells between lastSelected and current
          const [h1, d1] = parseCellKey(lastSelected);

          for (let d = Math.min(d1, day); d <= Math.max(d1, day); d++) {
            for (let h = Math.min(h1, hour); h <= Math.max(h1, hour); h++) {
              next.add(createCellKey(h, d));
            }
          }
        } else if (next.has(key)) {
          // Toggle single cell - remove if exists
          next.delete(key);
        } else {
          // Toggle single cell - add if doesn't exist
          next.add(key);
        }

        return next;
      });

      setLastSelected(key);
    },
    [lastSelected],
  );

  const clearSelection = useCallback(() => {
    setSelectedCells(new Set());
    setLastSelected(null);
  }, []);

  const startDrag = useCallback(
    (hour: number, day: number, shiftKey: boolean) => {
      // Just record the starting position - don't select yet
      // Selection only happens when mouse moves to a different cell
      setIsPointerDown(true);
      dragState.current = {
        isActive: false,
        startHour: hour,
        startDay: day,
        shiftKey,
        baseSelection: shiftKey ? new Set(selectedCells) : new Set(),
      };
    },
    [selectedCells],
  );

  const updateDrag = useCallback((hour: number, day: number) => {
    if (!dragState.current) return;

    const { startHour, startDay, shiftKey, baseSelection, isActive } = dragState.current;

    // Only activate drag when mouse moves to a DIFFERENT cell
    // This allows single clicks to work normally via onPress
    if (!isActive) {
      if (hour === startHour && day === startDay) return;
      dragState.current.isActive = true;
      setIsDragging(true);
    }

    // Compute rectangular range between start and current position
    const minHour = Math.min(startHour, hour);
    const maxHour = Math.max(startHour, hour);
    const minDay = Math.min(startDay, day);
    const maxDay = Math.max(startDay, day);

    const draggedCells = new Set<CellKey>();
    for (let d = minDay; d <= maxDay; d++) {
      for (let h = minHour; h <= maxHour; h++) {
        draggedCells.add(createCellKey(h, d));
      }
    }

    // Merge with base selection if Shift was held
    if (shiftKey) {
      setSelectedCells(new Set([...baseSelection, ...draggedCells]));
    } else {
      setSelectedCells(draggedCells);
    }
  }, []);

  const endDrag = useCallback(() => {
    if (dragState.current?.isActive) {
      setLastSelected(createCellKey(dragState.current.startHour, dragState.current.startDay));
    }
    dragState.current = null;
    setIsPointerDown(false);
    setIsDragging(false);
  }, []);

  return {
    selectedCells,
    selectedCount: selectedCells.size,
    isDragging,
    isPointerDown,
    toggleCell,
    clearSelection,
    startDrag,
    updateDrag,
    endDrag,
  };
}
